import React, { useState, useEffect } from 'react';
import { useSpring, animated } from '@react-spring/web';
import { useGesture } from '@use-gesture/react';
import Confetti from 'react-confetti';
import { useNavigate } from 'react-router-dom';
import Logo from './logo/hirefly-logo.png';
import { db, auth } from './firebase-config';
import { collection, doc, getDoc, getDocs, addDoc, query, where, setDoc, onSnapshot, updateDoc } from 'firebase/firestore';
import './EmployerFeed.css';
import './ApplicantCard.css';
import './StatsCard.css';
import LetterAvatar from './LetterAvatar';
import LoadingSpinner from './LoadingSpinner';

const DraggableCard = ({ children, onSwipeComplete, onRightSwipe, onLeftSwipe, className }) => {
  const [{ x }, api] = useSpring(() => ({
    x: 0,
    config: { tension: 200, friction: 20 }
  }));

  const bind = useGesture({
    onDrag: ({ down, movement: [mx], velocity }) => {
      const swipeThreshold = 150;

      if (!down && (Math.abs(mx) > swipeThreshold || velocity > 0.3)) {
        const direction = mx > 0 ? 1 : -1;
        if (direction === 1) {
          onRightSwipe();
        } else if (direction === -1) {
          onLeftSwipe();
        }
        api.start({ x: direction * window.innerWidth, onRest: () => onSwipeComplete(() => api.start({ x: 0 })) });
      } else if (down) {
        api.start({ x: mx });
      } else {
        api.start({ x: 0 });
      }
    }
  });

  return (
    <animated.div {...bind()} style={{ x }} className={`job-card ${className || ''}`}>
      {children}
    </animated.div>
  );
};

const EmployerFeed = () => {
  const navigate = useNavigate();
  const [userProfilePicture, setUserProfilePicture] = useState('');
  const [userName, setUserName] = useState('');
  const [userRole, setUserRole] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [jobs, setJobs] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [index, setIndex] = useState(0);
  const [showConfetti, setShowConfetti] = useState(false);
  const [showMessage, setShowMessage] = useState(false);
  const [popupMessage, setPopupMessage] = useState('');
  const [fadeOutConfetti, setFadeOutConfetti] = useState(false);
  const [windowSize, setWindowSize] = useState({ width: window.innerWidth, height: window.innerHeight });
  const [isLoading, setIsLoading] = useState(true);
  const [showMatchMessage, setShowMatchMessage] = useState(false);
  const [matchMessage, setMatchMessage] = useState('');
  const [error, setError] = useState('');
  const [employeeApplications, setEmployeeApplications] = useState({});
  const [swipedEmployeeIds, setSwipedEmployeeIds] = useState(new Set());
  const [stats, setStats] = useState({
    totalSwipes: 0,
    matches: 0,
    pendingReviews: 0
  });
  const [selectedJobId, setSelectedJobId] = useState('');
  const [filterValues, setFilterValues] = useState({
    experience: '',
    education: '',
    skills: [],
    location: '',
    keywords: ''
  });
  const [activeJobs, setActiveJobs] = useState([]);
  const [activeFilter, setActiveFilter] = useState(null);
  const [activeEmployeeTab, setActiveEmployeeTab] = useState('Experience');
  const filterOptions = [
    { key: 'experience', label: 'Experience' },
    { key: 'education', label: 'Education' },
    { key: 'skills', label: 'Skills' },
    { key: 'location', label: 'Location' },
  ];

  useEffect(() => {
    let isMounted = true;

    const fetchUserData = async () => {
      const user = auth.currentUser;
      if (!user) {
        navigate('/login');
        return;
      }

      try {
        // First check if user is an employee
        const employeeDoc = await getDoc(doc(db, 'employees', user.uid));
        if (employeeDoc.exists()) {
          if (isMounted) {
            setUserRole('employee');
          }
          const jobsQuery = query(collection(db, 'jobs'));
          const jobsSnapshot = await getDocs(jobsQuery);
          const jobsList = jobsSnapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data()
          }));
          if (isMounted) {
            setJobs(jobsList);
          }
          return;
        }

        // Then check if user is an employer
        const employerDoc = await getDoc(doc(db, 'employers', user.uid));
        if (employerDoc.exists()) {
          const employerData = employerDoc.data();
          if (isMounted) {
            setUserRole('employer');
            setUserName(employerData.companyName || '');
            setUserProfilePicture(employerData.profilePic || 'https://via.placeholder.com/40');
          }

          // Get all job postings for this employer
          const jobsQuery = query(collection(db, 'jobs'), where('employerId', '==', user.uid));
          const jobsSnapshot = await getDocs(jobsQuery);
          const jobIds = jobsSnapshot.docs.map(doc => doc.id);
          const jobsMap = {};
          jobsSnapshot.docs.forEach(doc => {
            jobsMap[doc.id] = doc.data();
          });

          // Get all applications for these jobs (batching for Firestore 'in' limit)
          let allApplications = [];
          for (let i = 0; i < jobIds.length; i += 30) {
            const batch = jobIds.slice(i, i + 30);
            const applicationsQuery = query(collection(db, 'applications'), where('jobId', 'in', batch));
            const applicationsSnapshot = await getDocs(applicationsQuery);
            allApplications = allApplications.concat(applicationsSnapshot.docs);
          }
          const employeeIds = [...new Set(allApplications.map(doc => doc.data().employeeId))];

          // Fetch swipes for this employer
          const swipesQuery = query(collection(db, 'employerSwipes'), where('employerId', '==', user.uid));
          const swipesSnapshot = await getDocs(swipesQuery);
          const swipedSet = new Set(swipesSnapshot.docs.map(doc => doc.data().employeeId + '-' + doc.data().jobId));

          // Map employeeId to their applications (with job title)
          const employeeApplicationsMap = {};
          allApplications.forEach(doc => {
            const app = doc.data();
            if (!employeeApplicationsMap[app.employeeId]) employeeApplicationsMap[app.employeeId] = [];
            employeeApplicationsMap[app.employeeId].push({
              jobTitle: jobsMap[app.jobId]?.title || 'Unknown',
              jobId: app.jobId,
              status: app.status || 'pending'
            });
          });

          // Get employee data for all applicants, filter out those already swiped for all their jobs
          const employeesList = await Promise.all(
            employeeIds.map(async (employeeId) => {
              const employeeDoc = await getDoc(doc(db, 'employees', employeeId));
              return {
                id: employeeId,
                ...employeeDoc.data()
              };
            })
          );

          // Only include employees who have at least one application not swiped on
          const filteredEmployees = employeesList.filter(emp => {
            const apps = employeeApplicationsMap[emp.id] || [];
            return apps.some(app => !swipedSet.has(emp.id + '-' + app.jobId));
          });

          if (isMounted) {
            setEmployees(filteredEmployees);
            setEmployeeApplications(employeeApplicationsMap);
            setSwipedEmployeeIds(swipedSet);
          }
          return;
        }

        // If neither exists, redirect to role selection
        navigate('/role-selection');
      } catch (error) {
        console.error('Error fetching user data:', error);
        if (isMounted) {
          setError('Failed to load data. Please try again.');
        }
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    fetchUserData();

    return () => {
      isMounted = false;
    };
  }, [navigate]);

  useEffect(() => {
    console.log('userName updated to:', userName);
  }, [userName]);

  useEffect(() => {
    let isMounted = true;
    const user = auth.currentUser;
    if (!user) return;

    // Set up real-time listeners for stats
    const unsubscribeSwipes = onSnapshot(
      query(collection(db, 'employerSwipes'), where('employerId', '==', user.uid)),
      (snapshot) => {
        if (isMounted) {
          const totalSwipes = snapshot.docs.length;
          setStats(prev => ({ ...prev, totalSwipes }));
        }
      }
    );

    const unsubscribeMatches = onSnapshot(
      query(
        collection(db, 'conversations'),
        where('employerId', '==', user.uid),
        where('isMatch', '==', true)
      ),
      (snapshot) => {
        if (isMounted) {
          const matches = snapshot.docs.length;
          setStats(prev => ({ ...prev, matches }));
        }
      }
    );

    const unsubscribePending = onSnapshot(
      query(
        collection(db, 'applications'),
        where('employerId', '==', user.uid),
        where('status', '==', 'pending')
      ),
      (snapshot) => {
        if (isMounted) {
          const pendingReviews = snapshot.docs.length;
          setStats(prev => ({ ...prev, pendingReviews }));
        }
      }
    );

    return () => {
      isMounted = false;
      unsubscribeSwipes();
      unsubscribeMatches();
      unsubscribePending();
    };
  }, []);

  const handleRightSwipe = async () => {
    const user = auth.currentUser;
    if (!user) return;

    try {
      // Get the current candidate's data
      const currentCandidate = employees[index];
      // Find the first application for this employer's jobs that hasn't been swiped on
      const apps = employeeApplications[currentCandidate.id] || [];
      const unswipedApp = apps.find(app => !swipedEmployeeIds.has(currentCandidate.id + '-' + app.jobId));
      if (!unswipedApp) return;

      // Record the swipe in Firestore
      await setDoc(doc(collection(db, 'employerSwipes')), {
        employerId: user.uid,
        employeeId: currentCandidate.id,
        jobId: unswipedApp.jobId,
        direction: 'right',
        swipedAt: new Date()
      });

      // Check if this candidate has already applied to any of the employer's jobs
      const applicationsQuery = query(
        collection(db, 'applications'),
        where('employeeId', '==', currentCandidate.id),
        where('status', '==', 'pending')
      );
      const applicationsSnapshot = await getDocs(applicationsQuery);
      const hasApplied = applicationsSnapshot.docs.some(doc => {
        const application = doc.data();
        return application.employerId === user.uid;
      });

      // Check if the employee has already swiped right on any of the employer's jobs
      const employeeSwipesQuery = query(
        collection(db, 'employeeSwipes'),
        where('employeeId', '==', currentCandidate.id),
        where('direction', '==', 'right')
      );
      const employeeSwipesSnapshot = await getDocs(employeeSwipesQuery);
      const hasSwipedRight = employeeSwipesSnapshot.docs.some(doc => {
        const swipe = doc.data();
        return swipe.employerId === user.uid;
      });

      // Only create a conversation if there's a mutual match (both have swiped right)
      if (hasApplied && hasSwipedRight) {
        // Update the application status to 'Conversation Started'
        const appQuery = query(
          collection(db, 'applications'),
          where('employeeId', '==', currentCandidate.id),
          where('employerId', '==', user.uid),
          where('jobId', '==', unswipedApp.jobId),
          where('status', '==', 'pending')
        );
        const appSnapshot = await getDocs(appQuery);
        for (const docSnap of appSnapshot.docs) {
          await updateDoc(doc(db, 'applications', docSnap.id), { status: 'Conversation Started' });
        }
        // Check for existing conversation
        const conversationsQuery = query(
          collection(db, 'conversations'),
          where('employerId', '==', user.uid),
          where('employeeId', '==', currentCandidate.id)
        );
        const conversationsSnapshot = await getDocs(conversationsQuery);
        let existingConversation = conversationsSnapshot.docs[0];

        if (existingConversation) {
          // Update existing conversation with new match
          const conversationRef = doc(db, 'conversations', existingConversation.id);
          await updateDoc(conversationRef, {
            lastMessage: `New match: ${unswipedApp.jobTitle}`,
            lastMessageTime: new Date(),
            isMatch: true
          });

          // Add new match message
          await addDoc(collection(db, 'conversations', existingConversation.id, 'messages'), {
            text: `New match: ${unswipedApp.jobTitle}`,
            senderId: user.uid,
            timestamp: new Date(),
            isSystemMessage: true
          });
        } else {
          // Create new conversation
          const chatRef = await addDoc(collection(db, 'conversations'), {
            participants: [user.uid, currentCandidate.id],
            createdAt: new Date(),
            lastMessage: `New match for ${unswipedApp.jobTitle}`,
            lastMessageTime: new Date(),
            employerId: user.uid,
            employeeId: currentCandidate.id,
            isMatch: true,
            employerName: userName || 'Unknown Employer',
            employeeName: currentCandidate.fullName || 'Unknown Employee',
            employerPic: null,
            employeePic: null
          });

          // Get the job details
          const jobDoc = await getDoc(doc(db, 'jobs', unswipedApp.jobId));
          const jobData = jobDoc.data();

          // Add welcome message with company details and view profile button
          const welcomeMessage = {
            text: `🎉 New match for ${jobData.title}!\n\n` +
              `Company Details:\n` +
              `Company: ${userName}\n` +
              `Position: ${jobData.title}\n` +
              `Location: ${jobData.location || 'Not specified'}\n` +
              `Salary: ${jobData.salary || 'Not specified'}\n` +
              `Job Type: ${jobData.jobType || 'Not specified'}\n\n` +
              `You can now start chatting with the employer!`,
            senderId: user.uid,
            timestamp: new Date(),
            isSystemMessage: true,
            actionButton: {
              type: 'viewJob',
              jobId: unswipedApp.jobId,
              label: 'View Job Posting'
            }
          };

          await addDoc(collection(db, 'conversations', chatRef.id, 'messages'), welcomeMessage);
        }

        // Show match message
        setMatchMessage("It's a match! 🎉");
        setShowMatchMessage(true);
      } else {
        // Regular right swipe behavior - no conversation created
        setShowConfetti(true);
        setPopupMessage('Request Sent!');
        setShowMessage(true);
      }

      // Remove the current employee from the feed if all their applications have been swiped on
      setEmployees(prevEmployees => {
        const newEmployees = prevEmployees.filter((emp, i) => {
          if (i !== index) return true;
          // Check if this swipe means all their applications are now swiped
          const apps = employeeApplications[emp.id] || [];
          const allSwiped = apps.every(app =>
            swipedEmployeeIds.has(emp.id + '-' + app.jobId) || (emp.id === currentCandidate.id && app.jobId === unswipedApp.jobId)
          );
          return !allSwiped;
        });
        setIndex(prevIndex => {
          if (newEmployees.length === 0) return 0;
          return prevIndex >= newEmployees.length ? 0 : prevIndex;
        });
        return newEmployees;
      });
      // Also update the swipedEmployeeIds state
      setSwipedEmployeeIds(prev => new Set([...prev, currentCandidate.id + '-' + unswipedApp.jobId]));
    } catch (error) {
      console.error('Error handling right swipe:', error);
    }
  };

  const handleLeftSwipe = async () => {
    const user = auth.currentUser;
    if (!user) return;

    try {
      // Get the current candidate's data
      const currentCandidate = employees[index];
      // Find the first application for this employer's jobs that hasn't been swiped on
      const apps = employeeApplications[currentCandidate.id] || [];
      const unswipedApp = apps.find(app => !swipedEmployeeIds.has(currentCandidate.id + '-' + app.jobId));
      if (!unswipedApp) return;

      // Record the swipe in Firestore
      await setDoc(doc(collection(db, 'employerSwipes')), {
        employerId: user.uid,
        employeeId: currentCandidate.id,
        jobId: unswipedApp.jobId,
        direction: 'left',
        swipedAt: new Date()
      });

      // Remove the current employee from the feed if all their applications have been swiped on
      setEmployees(prevEmployees => {
        const newEmployees = prevEmployees.filter((emp, i) => {
          if (i !== index) return true;
          // Check if this swipe means all their applications are now swiped
          const apps = employeeApplications[emp.id] || [];
          const allSwiped = apps.every(app =>
            swipedEmployeeIds.has(emp.id + '-' + app.jobId) || (emp.id === currentCandidate.id && app.jobId === unswipedApp.jobId)
          );
          return !allSwiped;
        });
        setIndex(prevIndex => {
          if (newEmployees.length === 0) return 0;
          return prevIndex >= newEmployees.length ? 0 : prevIndex;
        });
        return newEmployees;
      });
      setSwipedEmployeeIds(prev => new Set([...prev, currentCandidate.id + '-' + unswipedApp.jobId]));
    } catch (error) {
      console.error('Error handling left swipe:', error);
    }
  };

  const handleSwipeComplete = (resetCardPosition) => {
    setIndex((prevIndex) => (prevIndex + 1) % (userRole === 'employer' ? employees.length : jobs.length));
    resetCardPosition();
  };

  const handleSearch = (event) => {
    setSearchQuery(event.target.value);
  };

  useEffect(() => {
    const handleResize = () => {
      setWindowSize({ width: window.innerWidth, height: window.innerHeight });
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Fetch active jobs for dropdown
  useEffect(() => {
    const fetchActiveJobs = async () => {
      const user = auth.currentUser;
      if (!user) return;
      const jobsQuery = query(collection(db, 'jobs'), where('employerId', '==', user.uid), where('status', '==', 'active'));
      const jobsSnapshot = await getDocs(jobsQuery);
      setActiveJobs(jobsSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
      if (jobsSnapshot.docs.length > 0 && !selectedJobId) {
        setSelectedJobId(jobsSnapshot.docs[0].id);
      }
    };
    fetchActiveJobs();
  }, [selectedJobId]);

  // Filter employees by selected job and filters (OR logic)
  const filteredEmployees = React.useMemo(() => {
    if (!selectedJobId) return [];
    // Only show employees who applied to the selected job
    const applicants = employees.filter(emp => {
      const apps = employeeApplications[emp.id] || [];
      return apps.some(app => app.jobId === selectedJobId);
    });
    // If no filters, return all applicants
    const { experience, education, skills, location, keywords } = filterValues;
    if (!experience && !education && skills.length === 0 && !location && !keywords) return applicants;
    // OR logic: show if any filter matches
    return applicants.filter(emp => {
      const matchesExperience = experience && (emp.experience || '').toLowerCase().includes(experience.toLowerCase());
      const matchesEducation = education && (emp.education || '').toLowerCase().includes(education.toLowerCase());
      const matchesSkills = skills.length > 0 && skills.some(skill => (emp.skills || '').toLowerCase().includes(skill.toLowerCase()));
      const matchesLocation = location && (emp.location || '').toLowerCase().includes(location.toLowerCase());
      const matchesKeywords = keywords && (
        (emp.skills || '').toLowerCase().includes(keywords.toLowerCase()) ||
        (emp.experience || '').toLowerCase().includes(keywords.toLowerCase()) ||
        (emp.education || '').toLowerCase().includes(keywords.toLowerCase())
      );
      return matchesExperience || matchesEducation || matchesSkills || matchesLocation || matchesKeywords;
    });
  }, [employees, employeeApplications, selectedJobId, filterValues]);

  // Add a loading state to prevent flickering
  if (isLoading) {
    return <LoadingSpinner />;
  }

  return (
    <div className="job-feed">
      <div className="header">
        <div className="logo-section">
          <img src={Logo} alt="Hirefly Logo" className="logo" />
          <h1 className="hirefly-text">Hirefly</h1>
        </div>
        <div className="search-section">
          <select
            className="search-input"
            value={selectedJobId}
            onChange={e => setSelectedJobId(e.target.value)}
          >
            {activeJobs.map(job => (
              <option key={job.id} value={job.id}>{job.title} @ {job.company}</option>
            ))}
          </select>
          <div className="filters-row">
            {filterOptions.map(opt => (
              <button
                key={opt.key}
                className={`filter-button${activeFilter === opt.key ? ' active' : ''}${filterValues[opt.key] && (typeof filterValues[opt.key] === 'string' ? filterValues[opt.key].length > 0 : Array.isArray(filterValues[opt.key]) && filterValues[opt.key].length > 0) ? ' selected' : ''}`}
                onClick={() => setActiveFilter(activeFilter === opt.key ? null : opt.key)}
              >
                {opt.label}
                {/* Show value if set */}
                {filterValues[opt.key] && typeof filterValues[opt.key] === 'string' && filterValues[opt.key].length > 0 && (
                  <span className="clear-filter" onClick={e => { e.stopPropagation(); setFilterValues(f => ({ ...f, [opt.key]: '' })); }}>×</span>
                )}
                {opt.key === 'skills' && Array.isArray(filterValues.skills) && filterValues.skills.length > 0 && (
                  <span className="clear-filter" onClick={e => { e.stopPropagation(); setFilterValues(f => ({ ...f, skills: [] })); }}>×</span>
                )}
              </button>
            ))}
          </div>
          {activeFilter && (
            <div className="filter-dropdown">
              {activeFilter === 'skills' ? (
                <input
                  type="text"
                  autoFocus
                  placeholder="Enter skills (comma separated)"
                  value={filterValues.skills.join(', ')}
                  onChange={e => setFilterValues(f => ({ ...f, skills: e.target.value.split(',').map(s => s.trim()).filter(Boolean) }))}
                  className="search-input"
                  onBlur={() => setActiveFilter(null)}
                />
              ) : (
                <input
                  type="text"
                  autoFocus
                  placeholder={`Enter ${filterOptions.find(f => f.key === activeFilter)?.label || ''}`}
                  value={filterValues[activeFilter] || ''}
                  onChange={e => setFilterValues(f => ({ ...f, [activeFilter]: e.target.value }))}
                  className="search-input"
                  onBlur={() => setActiveFilter(null)}
                />
              )}
              <button
                style={{ marginLeft: 4 }}
                className="filter-button"
                onMouseDown={e => { e.preventDefault(); setFilterValues(f => ({ ...f, [activeFilter]: activeFilter === 'skills' ? [] : '' })); setActiveFilter(null); }}
                title="Clear filter"
              >Clear</button>
            </div>
          )}
        </div>
        <div className="profile-section" onClick={() => navigate('/profile')}>
          {userProfilePicture ? (
            <img src={userProfilePicture} alt="Profile" className="profile-picture" />
          ) : (
            <LetterAvatar 
              name={userName || 'Unknown'} 
              size={44}
              className="profile-picture"
            />
          )}
        </div>
      </div>
      <div className="card-container">
        <DraggableCard onSwipeComplete={handleSwipeComplete} onRightSwipe={handleRightSwipe} onLeftSwipe={handleLeftSwipe} className={`job-card ${!filteredEmployees.length ? 'empty-card' : ''}`}>
          {userRole === 'employer' ? (
            filteredEmployees.length > 0 ? (
              <div className="job-card-grid">
                {/* Top Row: Logo and Name/Location */}
                <div className="job-card-grid-row top-row">
                  <div className="job-card-logo-box" style={{ overflow: 'hidden', padding: 0, border: 'none' }}>
                    {filteredEmployees[index]?.profilePic && filteredEmployees[index].profilePic !== 'https://via.placeholder.com/40' ? (
                      <img
                        src={filteredEmployees[index].profilePic}
                        alt={`${filteredEmployees[index]?.fullName} profile`}
                        className="job-logo-full"
                        style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                      />
                    ) : (
                      <LetterAvatar 
                        name={filteredEmployees[index]?.fullName || 'Unknown'} 
                        size={90}
                        className="job-logo-square"
                        style={{ borderRadius: '16px 0 0 16px', width: '100%', height: '100%' }}
                      />
                    )}
                  </div>
                  <div className="job-card-title-company-box">
                    <div className="job-card-title">{filteredEmployees[index]?.fullName}</div>
                    <div className="job-card-company">{filteredEmployees[index]?.location}</div>
                  </div>
                </div>
                {/* Tabs for Employee Details */}
                <div className="job-card-tabs">
                  <button className={`job-card-tab${activeEmployeeTab === 'Experience' ? ' active' : ''}`} onClick={() => setActiveEmployeeTab('Experience')}>Experience</button>
                  <button className={`job-card-tab${activeEmployeeTab === 'Skills' ? ' active' : ''}`} onClick={() => setActiveEmployeeTab('Skills')}>Skills</button>
                  <button className={`job-card-tab${activeEmployeeTab === 'Education' ? ' active' : ''}`} onClick={() => setActiveEmployeeTab('Education')}>Education</button>
                </div>
                <div className="job-card-tab-content">
                  {activeEmployeeTab === 'Experience' && (
                    <div>{filteredEmployees[index]?.experience || 'No experience listed.'}</div>
                  )}
                  {activeEmployeeTab === 'Skills' && (
                    <div>{filteredEmployees[index]?.skills || 'No skills listed.'}</div>
                  )}
                  {activeEmployeeTab === 'Education' && (
                    <div>
                      <div>{filteredEmployees[index]?.education || 'No education listed.'}</div>
                      <div style={{ marginTop: '18px' }}>
                        <div style={{ fontWeight: 700, fontSize: '1rem', marginBottom: 6 }}>Certifications</div>
                        {filteredEmployees[index]?.certifications && Array.isArray(filteredEmployees[index].certifications) && filteredEmployees[index].certifications.length > 0 ? (
                          <ul style={{ paddingLeft: 18, margin: 0 }}>
                            {filteredEmployees[index].certifications.map((cert, i) => (
                              <li key={i}>{cert}</li>
                            ))}
                          </ul>
                        ) : (
                          <div>No certifications listed.</div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <div className="empty-state">
                <div className="empty-state-icon">👥</div>
                <div className="empty-state-title">No Candidates Found</div>
                <div className="empty-state-message">
                  No candidates found matching your search. Try adjusting your filters or check back later for new applicants!
                </div>
              </div>
            )
          ) : (
            jobs.length > 0 ? (
              <>
                <img
                  src={jobs[index]?.logo || 'https://via.placeholder.com/50'}
                  alt={`${jobs[index]?.company} logo`}
                  className="job-logo"
                />
                <h3>{jobs[index]?.title}</h3>
                <p>{jobs[index]?.company}</p>
                {/* Salary and Location Row */}
                <div className="job-card-grid-row salary-location-row">
                  <div className="job-card-salary-box">
                    <div className="job-card-label">Salary:</div>
                    <div className="job-card-value">{jobs[index]?.salary || 'No salary provided.'}</div>
                  </div>
                  <div className="job-card-location-box">
                    <div className="job-card-label">Location:</div>
                    <div className="job-card-value">{jobs[index]?.location || 'No location provided.'}</div>
                  </div>
                </div>
                {/* Organized Labels */}
                <div className="job-details">
                  <h4>Description:</h4>
                  <p>{jobs[index]?.description || 'No description provided.'}</p>
                  <h4>Requirements:</h4>
                  <p>{jobs[index]?.requirements || 'No requirements listed.'}</p>
                  <h4>Why Work Here?</h4>
                  <ul>
                    {Array.isArray(jobs[index]?.reasonsToJoin) && jobs[index].reasonsToJoin.length > 0 ? (
                      jobs[index].reasonsToJoin.map((reason, i) => (
                        <li key={i}>{reason}</li>
                      ))
                    ) : (
                      <li>No reasons listed.</li>
                    )}
                  </ul>
                </div>
              </>
            ) : (
              <div className="empty-state">
                <div className="empty-state-icon">💼</div>
                <div className="empty-state-title">No Jobs Available</div>
                <div className="empty-state-message">
                  There are no job postings available at the moment. Check back later!
                </div>
              </div>
            )
          )}
        </DraggableCard>
      </div>

      {showConfetti && (
        <Confetti
          width={windowSize.width}
          height={windowSize.height}
          numberOfPieces={150}
          recycle={false}
          style={{
            opacity: fadeOutConfetti ? 0 : 1,
            transition: 'opacity 0.5s ease-out',
          }}
        />
      )}

      {showMessage && (
        <div className="application-message">
          {popupMessage}
        </div>
      )}

      {showMatchMessage && (
        <div className="match-message">
          {matchMessage}
        </div>
      )}
    </div>
  );
};

export default EmployerFeed;